<template>
  <v-dialog v-model="value" width="650" persistent>
    <v-form v-model="valid" ref="form">
      <v-card class="w-100 rounded-lg">
        <v-card-title>{{ $t('spedilink.groups.add') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.name"
            :label="$t('spedilink.groups.enterName')"
            outlined
            dense
            class="mb-2"
            :rules="requiredRules"
          />
          <v-textarea
            v-model="form.description"
            :label="$t('spedilink.groups.enterDescription')"
            outlined
            rows="2"
            class="mb-4"
          />

          <v-checkbox
            v-model="form.companyShare"
            :label="$t('spedilink.groups.companyShare')"
            hide-details
            class="mt-0 mb-4"
          />

          <div class="text-subtitle-2 mb-2">{{ $t('spedilink.groups.selectPersons') }}</div>

          <v-text-field
            v-model="personSearch"
            prepend-inner-icon="$magnify"
            :placeholder="$t('spedilink.groups.searchPersons')"
            outlined
            dense
            clearable
            hide-details
            class="mb-2"
          />

          <v-card outlined class="persons-list">
            <v-progress-linear v-if="loadingPersons" indeterminate color="primary" />
            <v-list dense class="py-0">
              <template v-if="filteredPersons.length === 0">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="grey--text text-caption">
                      {{ $t('spedilink.groups.noPersonsFound') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-else>
                <v-list-item
                  v-for="person in filteredPersons"
                  :key="person.id"
                  @click="togglePerson(person.id)"
                  dense
                >
                  <v-list-item-action class="mr-3 my-0">
                    <v-checkbox
                      :input-value="form.userIds.includes(person.id)"
                      color="primary"
                      dense
                      hide-details
                      @click.stop="togglePerson(person.id)"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ person.firstName }} {{ person.lastName }}</v-list-item-title>
                    <v-list-item-subtitle>{{ person.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list>
          </v-card>

          <div v-if="form.userIds.length > 0" class="mt-2 text-caption grey--text">
            {{ $t('spedilink.groups.selectedCount', { count: form.userIds.length }) }}
          </div>
        </v-card-text>
        <v-card-actions class="d-flex justify-end px-4 pb-4">
          <v-btn outlined :disabled="loading" @click="close()">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            class="ml-2"
            :loading="loading"
            :disabled="loading"
            depressed
            @click="save()"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import Service from '@/services'

export default {
  name: 'AddSpediLinkGroupDialog',
  props: {
    value: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      valid: true,
      loading: false,
      loadingPersons: false,
      persons: [],
      personSearch: '',
      form: {
        name: '',
        description: '',
        userIds: [],
        companyShare: false
      },
      requiredRules: [
        (v) => !!v || this.$t('validation.fieldRequired')
      ]
    }
  },
  computed: {
    filteredPersons () {
      if (!this.personSearch) return this.persons
      const search = this.personSearch.toLowerCase()
      return this.persons.filter(p =>
        p.firstName.toLowerCase().includes(search) ||
        p.lastName.toLowerCase().includes(search) ||
        p.email.toLowerCase().includes(search)
      )
    }
  },
  methods: {
    async loadPersons () {
      this.loadingPersons = true
      try {
        const { data } = await Service.get('spedilink/SpediLinkUsers/GetList', {
          page: 1,
          pageSize: 1000
        })
        this.persons = data.results
      } catch (e) {
      } finally {
        this.loadingPersons = false
      }
    },
    togglePerson (personId) {
      const index = this.form.userIds.indexOf(personId)
      if (index === -1) {
        this.form.userIds.push(personId)
      } else {
        this.form.userIds.splice(index, 1)
      }
    },
    close () {
      this.$emit('close')
      this.resetForm()
    },
    resetForm () {
      this.form = { name: '', description: '', userIds: [], companyShare: false }
      this.personSearch = ''
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    async save () {
      this.$refs.form.validate()
      if (!this.valid) return

      this.loading = true
      try {
        await Service.post('spedilink/SpediLinkGroups/Create', {
          name: this.form.name,
          description: this.form.description || undefined,
          userIds: this.form.userIds,
          companyShare: this.form.companyShare
        })
        await this.$store.dispatch('setSuccessNotification', this.$t('common.confirmOperation'))
        this.$emit('group-added')
        this.resetForm()
      } catch (e) {
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    value (newVal) {
      if (!newVal) {
        this.resetForm()
      } else {
        this.loadPersons()
      }
    }
  }
}
</script>

<style scoped>
.persons-list {
  max-height: 240px;
  overflow-y: auto;
}
</style>
